import BluebirdPromise from 'bluebird';
import { ApiError } from 'app/scripts/network/api-error';
// eslint-disable-next-line no-restricted-imports
import { Batcher } from 'app/scripts/network/rpc/batcher';
// eslint-disable-next-line no-restricted-imports
import { trelloBatchFetch } from './trelloBatchFetch';
import { sendErrorEvent } from '@trello/error-reporting';
import { featureFlagClient } from '@trello/feature-flag-client';

export const sendVerificationRequests = async ({
  data,
  onApiError,
}: {
  data: {
    id: string;
    url: string;
  }[];
  onApiError: (idModelInvalid: string) => void;
}) => {
  if (data.length === 0) {
    return BluebirdPromise.resolve();
  }

  if (featureFlagClient.get('fep.batcher-v2', false)) {
    const urls = data.map((item) => item.url);
    const urlToIdHash = new Map<string, string>();
    data.forEach((item) => urlToIdHash.set(item.url, item.id));

    try {
      const results = await trelloBatchFetch(urls, {
        operationName: 'verificationRequests',
      });

      for (const [url, error] of results) {
        if (error) {
          onApiError(urlToIdHash.get(url) as string);
          continue;
        }
      }
    } catch (error) {
      // This is a connectivity problem that doesn't need to be reported, we'll just wait
      // for the next poll
      if (
        error instanceof ApiError.NoResponse ||
        error instanceof ApiError.Timeout
      ) {
        return BluebirdPromise.resolve();
      }

      // The whole request failed because we aren't authenticated anymore, we don't need
      // to report that
      if (error instanceof ApiError.Unauthenticated) {
        return BluebirdPromise.resolve();
      }

      sendErrorEvent(
        error || new Error(`Batch Failure while sending verification requests`),
        {
          extraData: {
            errorName: error.name,
            verifyData: JSON.stringify(data),
          },
        },
      );
    }

    return BluebirdPromise.resolve();
  }

  const batcher = new Batcher({
    headers: {
      'X-Trello-Operation-Source': 'rpc',
      'X-Trello-Operation-Name': 'verificationRequests',
    },
  });

  data.forEach((item) => {
    if (!item) {
      return;
    }
    const { id, url } = item;
    return batcher
      .request(url)
      .catch(ApiError, () => {
        return onApiError(id);
      })
      .catch(Batcher.BatchFailure, (batchError) => {
        // We'll handle the batch failure once, after the .send() call
      });
  });

  return batcher
    .send()
    .catch(ApiError.NoResponse, ApiError.Timeout, () => {
      // This is a connectivity problem that doesn't need to be reported, we'll just wait
      // for the next poll
    })
    .catch(ApiError.Unauthenticated, () => {
      // The whole request failed because we aren't authenticated anymore, we don't need
      // to report that
    })
    .catch(ApiError, (error: Error) => {
      sendErrorEvent(
        error || new Error(`Batch Failure while sending verification requests`),
        {
          extraData: {
            errorName: error.name,
            verifyData: JSON.stringify(data),
          },
        },
      );
    });
};
